import React, { useEffect, useState } from "react";
import api, { fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useSettings } from "@/context/SettingsContext";
import { Landmark, Phone, Globe, Send, Loader2 } from "lucide-react";

export default function HomeLoans() {
  const { feature } = useSettings();
  const [providers, setProviders] = useState([]);
  const [form, setForm] = useState({ name: "", phone: "", loan_amount: "", property_details: "", provider_id: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (feature("home_loans")) api.get("/loan-providers").then((r) => setProviders(r.data.providers || [])).catch((e) => console.debug("loan providers load failed", e));
  }, [feature]);

  const submit = async () => {
    if (!form.name.trim() || !form.phone.trim()) { toast.error("Name and phone are required"); return; }
    setSubmitting(true);
    try {
      await api.post("/loan-enquiries", form);
      toast.success("Loan enquiry submitted! Our team will contact you shortly.");
      setForm({ name: "", phone: "", loan_amount: "", property_details: "", provider_id: "" });
    } catch (e) { toast.error("Could not submit, please try again"); } finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12" data-testid="home-loans-page">
      {!feature("home_loans") ? (
        <div className="text-center py-24" data-testid="home-loans-disabled">
          <Landmark className="w-12 h-12 text-brand-muted mx-auto mb-4" />
          <h1 className="font-heading font-bold text-brand-ink text-2xl">Home Loans are currently unavailable</h1>
          <p className="text-brand-muted mt-2">Please check back later.</p>
        </div>
      ) : (
      <>
      <div className="mb-8">
        <h1 className="font-heading font-bold text-brand-ink text-3xl sm:text-4xl flex items-center gap-2"><Landmark className="w-8 h-8 text-brand-emerald" /> Home Loans</h1>
        <p className="text-brand-muted mt-2">Compare trusted lenders and send a quick loan enquiry.</p>
        <a href="/list-your-loan-service" className="inline-block mt-3"><Button variant="outline" className="rounded-full" data-testid="list-loan-service">List your loan service →</Button></a>
      </div>

      <div className="grid lg:grid-cols-[1fr_400px] gap-8">
        <div>
          <h2 className="font-heading font-semibold text-lg text-brand-ink">Loan Providers</h2>
          <p className="text-xs text-brand-muted mb-4 mt-0.5">Sorted by lowest interest rate — best deal first.</p>
          {providers.length === 0 ? <p className="text-brand-muted">No loan providers listed yet.</p> : (
            <div className="grid gap-4 sm:grid-cols-2">
              {providers.map((p, idx) => (
                <div key={p.id} className="bg-card border border-brand-line rounded-2xl p-5 relative" data-testid={`loan-provider-${p.id}`}>
                  {idx === 0 && p.interest_rate && (
                    <span className="absolute top-3 right-3 text-[10px] font-bold uppercase tracking-wide bg-brand-emerald text-white px-2 py-0.5 rounded-full" data-testid="best-rate-badge">Best rate</span>
                  )}
                  <div className="flex items-center gap-3">
                    {p.logo
                      ? <img src={fileUrl(p.logo)} alt={p.name} className="w-14 h-14 rounded-xl object-contain bg-white border border-brand-line shrink-0" />
                      : <div className="w-14 h-14 rounded-xl bg-brand-emerald/10 flex items-center justify-center shrink-0"><Landmark className="w-6 h-6 text-brand-emerald" /></div>}
                    <div className="min-w-0">
                      <h3 className="font-heading font-semibold text-brand-ink truncate">{p.name}</h3>
                      {p.contact && <p className="text-sm text-brand-muted flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{p.contact}</p>}
                    </div>
                  </div>
                  {p.interest_rate && <p className="text-sm text-brand-emerald font-semibold mt-3">Interest {p.interest_rate}</p>}
                  {p.website && <a href={p.website} target="_blank" rel="noopener noreferrer" className="mt-4 block"><Button className="w-full rounded-full bg-brand-emerald"><Globe className="w-4 h-4 mr-1.5" />Visit Website</Button></a>}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-card border border-brand-line rounded-2xl p-6 h-fit lg:sticky lg:top-20">
          <h2 className="font-heading font-semibold text-lg text-brand-ink mb-4">Loan Enquiry</h2>
          <div className="space-y-3">
            <div><Label>Your Name</Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="mt-1.5" data-testid="loan-name" /></div>
            <div><Label>Phone Number</Label><Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} className="mt-1.5" data-testid="loan-phone" /></div>
            <div><Label>Loan Amount Needed</Label><Input value={form.loan_amount} onChange={(e) => setForm((f) => ({ ...f, loan_amount: e.target.value }))} placeholder="e.g. ₹25,00,000" className="mt-1.5" data-testid="loan-amount" /></div>
            <div><Label>Property Details</Label><Textarea value={form.property_details} onChange={(e) => setForm((f) => ({ ...f, property_details: e.target.value }))} placeholder="Location, type, budget..." className="mt-1.5" data-testid="loan-property" /></div>
            {providers.length > 0 && (
              <div>
                <Label>Preferred Provider <span className="text-brand-muted text-xs">(optional)</span></Label>
                <Select value={form.provider_id} onValueChange={(v) => setForm((f) => ({ ...f, provider_id: v }))}>
                  <SelectTrigger className="mt-1.5" data-testid="loan-provider-select"><SelectValue placeholder="Any provider" /></SelectTrigger>
                  <SelectContent>{providers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <Button onClick={submit} disabled={submitting} className="w-full rounded-full bg-brand-terracotta hover:bg-brand-terracottaHover" data-testid="loan-submit">
              {submitting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}Submit Enquiry
            </Button>
          </div>
        </div>
      </div>
      </>
      )}
    </div>
  );
}
